import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageSquare, Star, Clock, XCircle } from "lucide-react";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import AccountLayout from "./AccountLayout";

export default function AccountSummary() {
  useSeoMeta({ pageType: "user_dashboard" });
  const { user } = useAuth();

  const { data: myReviews, isLoading } = useQuery({
    queryKey: ["my-reviews", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("id, status")
        .eq("user_id", user!.id);
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const counts = {
    total: myReviews?.length ?? 0,
    approved: myReviews?.filter((r) => r.status === "approved").length ?? 0,
    pending: myReviews?.filter((r) => r.status === "pending").length ?? 0,
    rejected: myReviews?.filter((r) => r.status === "rejected").length ?? 0,
  };

  const cards = [
    { label: "Reviews enviadas", count: counts.total, icon: MessageSquare, color: "bg-primary/10 text-primary" },
    { label: "Aprobadas", count: counts.approved, icon: Star, color: "bg-success/10 text-success" },
    { label: "Pendientes", count: counts.pending, icon: Clock, color: "bg-warning/10 text-warning" },
    { label: "Rechazadas", count: counts.rejected, icon: XCircle, color: "bg-destructive/10 text-destructive" },
  ];

  return (
    <AccountLayout>
      {isLoading ? (
        <Skeleton className="h-32" />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map((c) => (
              <Card key={c.label}>
                <CardContent className="flex items-center gap-3 p-5">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${c.color}`}>
                    <c.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-display font-bold">{c.count}</p>
                    <p className="text-xs text-muted-foreground">{c.label}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {counts.total === 0 && (
            <p className="mt-6 text-sm text-muted-foreground">
              Aún no has enviado ninguna review.{" "}
              <Link to="/" className="text-primary underline">Busca una sala</Link> para contribuir.
            </p>
          )}
        </>
      )}
    </AccountLayout>
  );
}
